import './styles/global.css';

import { createRoot, type Root } from 'react-dom/client';

import { App } from '@/App';
import { AppLoading } from '@/AppLoading';
import { initI18n } from '@/l10n/i18n';
import { bootstrapApp } from '@/presentation/stores/appBootstrap';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';

/**
 * Точка входа — перенос `void main()` из `lib/main.dart`.
 *
 * Что чем стало:
 *
 *   appRunner(RunnerConfiguration(...))  -> обычный `createRoot().render()`;
 *     пакет `app_runner` поднимал зону с перехватом ошибок, в вебе её роль
 *     играют слушатели `error` и `unhandledrejection` ниже.
 *   onFlutterError: FlutterError.presentError -> [presentError]
 *   AppBuilder(preInitialize: _initialize)    -> `bootstrapApp()` между
 *     двумя рендерами: сначала заставка, потом приложение.
 *   EasyLoading.instance.userInteractions = false -> уже заложено в
 *     `LoadingOverlayHost`: слой забирает касания на себя, отдельной
 *     настройки не осталось.
 *
 * Из `_initialize` убрана вся Android-часть: `KioskService.enableKiosk`,
 * `KioskService.setThisAppAsDefaultLauncher`, `SyncWakelockManager` и
 * `SystemChrome.setEnabledSystemUIMode(immersiveSticky)`. Это уже учтено
 * в `appBootstrap.ts`.
 *
 * `StrictMode` намеренно не ставится: он монтирует дерево дважды, а
 * перенесённые `initState`/`dispose` — это соединения чата, запись с
 * микрофона и таймеры сеанса. Второй прогон поднял бы их по два раза,
 * чего во Flutter не было.
 */

const ROOT_ELEMENT_ID = 'root';
const SPLASH_ELEMENT_ID = 'splash';

/** Столько длится затухание `#splash` — см. `<style>` в `index.html`. */
const SPLASH_FADE_MS = 250;

function describeError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}

/**
 * `FlutterError.presentError(errorDetails)` — сбой печатается в консоль
 * и попадает в журнал для разработчика, чтобы его было видно и на самом
 * планшете, без подключения к нему.
 */
function presentError(error: unknown, context: string): void {
  console.error(`[${context}]`, error);
  DiagnosticsLog.instance.event(`${context}: ${describeError(error)}`, {
    isError: true,
  });
}

function installErrorHandlers(): void {
  window.addEventListener('error', (event: ErrorEvent) => {
    presentError(event.error ?? event.message, 'необработанная ошибка');
  });
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    presentError(event.reason, 'необработанный отказ промиса');
  });
}

function mountRoot(): Root {
  const container = document.getElementById(ROOT_ELEMENT_ID);
  if (container === null) {
    throw new Error(`Не найден элемент #${ROOT_ELEMENT_ID}: проверьте index.html`);
  }
  return createRoot(container);
}

/** Два кадра: первый — отрисовка, второй — уже после неё. */
function afterPaint(callback: () => void): void {
  requestAnimationFrame(() => requestAnimationFrame(callback));
}

/**
 * Снятие экрана запуска — замена `flutter_native_splash.remove()`.
 *
 * Сначала класс `hidden` (затухание 250 мс), потом элемент убирается
 * совсем: полноэкранный слой поверх приложения не нужен даже прозрачным.
 */
function hideSplash(): void {
  const splash = document.getElementById(SPLASH_ELEMENT_ID);
  if (splash === null) return;
  splash.classList.add('hidden');
  window.setTimeout(() => splash.remove(), SPLASH_FADE_MS);
}

async function start(): Promise<void> {
  installErrorHandlers();
  // Переводы поднимаются до первого кадра — как делегат `AppLocalizations`
  // в `MaterialApp`. `bootstrapApp` зовёт `initI18n` ещё раз, но промис
  // там кешируется, так что работа делается один раз.
  await initI18n();

  const root = mountRoot();
  root.render(<AppLoading />);
  afterPaint(hideSplash);

  // `preInitialize: _initialize` — возвращается только когда всё готово.
  const initialState = await bootstrapApp();
  root.render(<App initialState={initialState} />);
}

void start().catch((error: unknown) => {
  presentError(error, 'запуск приложения');
});
